import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { colors, fonts } from "../theme";
import { categories } from "../data";
import { Eyebrow, Btn, PhoneLink } from "../ui";
import ProductEnquiryModal from "../ProductEnquiryModal";
import { canSubmitDirectly } from "../enquiry";
import FaqSection from "../FaqSection";
import NotFound from "./NotFound";
import Seo from "../../seo/Seo";
import { productMeta, hasProductPage, productPath } from "../../seo/productMeta";
import { absUrl } from "../../seo/site";
import {
  webPageSchema,
  breadcrumbSchema,
  specListSchema,
  faqSchema,
} from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

/**
 * A single grade — the page that answers "art card supplier in delhi" or
 * "300 gsm textured paper price" far better than a category listing can.
 *
 * The page only renders when src/seo/productMeta.js has real copy for it; see
 * the note there about why this is gated rather than templated.
 */
export default function ProductPage() {
  const { slug, productSlug } = useParams();
  const [enquiring, setEnquiring] = useState(false);

  const cat = categories.find((c) => c.slug === slug);
  const item = cat && cat.items.find((p) => p.slug === productSlug);
  const meta = cat && item ? productMeta(cat.slug, item.slug) : null;

  // No category, no product, or no written copy — all genuinely 404.
  if (!cat || !item || !meta) return <NotFound />;

  const path = productPath(cat.slug, item.slug);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Catalogue", path: "/catalogue" },
    { name: cat.name, path: `/catalogue/${cat.slug}` },
    { name: item.name, path },
  ];

  // Siblings in the same category, for internal linking out of this page.
  const siblings = cat.items.filter(
    (p) => p.slug !== item.slug && hasProductPage(cat.slug, p.slug)
  );

  return (
    <main
      className="se-page"
      style={{
        ...wrap,
        padding: "clamp(36px,8vw,60px) var(--sp-gutter) clamp(48px,11vw,84px)",
      }}
    >
      <Seo
        title={meta.title}
        description={meta.description}
        path={path}
        image={item.image}
        imageAlt={item.alt || item.name}
        type="article"
        schema={[
          webPageSchema({
            path,
            name: meta.title,
            description: meta.description,
            type: "ItemPage",
            primaryImage: item.image,
            breadcrumbId: `${absUrl(path)}#breadcrumb`,
          }),
          breadcrumbSchema(crumbs, path),
          specListSchema({ path, name: item.name, specs: meta.specs }),
          meta.faqs && meta.faqs.length ? faqSchema(meta.faqs, path) : null,
        ]}
      />

      {/* .se-crumbs lets the trail wrap below 700px and blocks each crumb out
          to a 44px target — the four-level product trail is the longest on the
          site and was breaking mid-label on a phone. */}
      <nav
        className="se-crumbs"
        aria-label="Breadcrumb"
        style={{ fontSize: "var(--fs-meta)", color: colors.muted2, marginBottom: "18px" }}
      >
        <Link to="/" style={{ color: colors.brown }}>Home</Link>
        <span style={{ margin: "0 8px" }}>/</span>
        <Link to="/catalogue" style={{ color: colors.brown }}>Catalogue</Link>
        <span style={{ margin: "0 8px" }}>/</span>
        <Link to={`/catalogue/${cat.slug}`} style={{ color: colors.brown }}>
          {cat.name}
        </Link>
        <span style={{ margin: "0 8px" }}>/</span>
        <span aria-current="page">{item.name}</span>
      </nav>

      {/* Same desktop split as .se-about2, but below 860px .se-product2 also
          pulls the last child — the photo and the enquiry CTA — above the copy.
          The spec sheet reads first on desktop by design; on a phone that put
          the page's only CTA about 2,300px down. `order` moves the painted box
          only, so the DOM order a crawler reads is untouched. */}
      <div
        className="se-product2"
        style={{
          display: "grid",
          gridTemplateColumns: "1.15fr 0.85fr",
          gap: "52px",
          alignItems: "start",
        }}
      >
        <div>
          <Eyebrow>{cat.name}</Eyebrow>
          <h1
            style={{
              fontFamily: fonts.serif,
              fontSize: "var(--fs-h1-alt)",
              fontWeight: 500,
              margin: "12px 0 18px",
              lineHeight: 1.12,
              maxWidth: "20ch",
            }}
          >
            {meta.h1}
          </h1>
          {/* 11 of the 22 intros are authored as several paragraphs separated
              by a blank line in productMeta.js. Rendered into a single <p> the
              blank line is just whitespace, which HTML collapses to one space,
              so those products showed one 200-word slab — around 40 unbroken
              lines at 320px before the first subheading.

              Splitting on the blank line is a rendering change only: the words,
              their order and the surrounding markup are untouched, and the
              text a crawler extracts is identical because the paragraph break
              serialises to the same single space the collapsed newline did.
              The data in productMeta.js is not edited, so the intro that feeds
              the meta description and the JSON-LD description is unchanged. */}
          {meta.intro.split("\n\n").map((para, i, all) => (
            <p
              key={para.slice(0, 32)}
              style={{
                fontSize: "var(--fs-body)",
                lineHeight: 1.7,
                color: colors.ink3,
                // Only the last paragraph carries the 26px gap to whatever
                // follows; the others take a tighter inter-paragraph rhythm.
                margin: i === all.length - 1 ? "0 0 26px" : "0 0 16px",
                maxWidth: "62ch",
              }}
            >
              {para}
            </p>
          ))}

          {meta.specs && meta.specs.length > 0 && (
            <>
              <h2
                style={{
                  fontFamily: fonts.serif,
                  fontSize: "22px",
                  fontWeight: 600,
                  margin: "32px 0 14px",
                }}
              >
                Specifications
              </h2>
              {/* The column widths live in .se-spec, not here. `auto 1fr` gave
                  the label track its max-content first, so the 196px
                  "International equivalent" left the value column 78px at
                  320px — one word per line, one value 162px tall. Below 560px
                  the class drops it to a single stacked column. */}
              <dl
                className="se-spec"
                style={{
                  display: "grid",
                  gap: "10px 24px",
                  margin: 0,
                  fontSize: "var(--fs-body)",
                  borderTop: `1px solid ${colors.border}`,
                  paddingTop: "16px",
                }}
              >
                {meta.specs.map((s) => (
                  <React.Fragment key={s.label}>
                    <dt style={{ color: colors.muted2, fontFamily: fonts.mono, fontSize: "var(--fs-label)", letterSpacing: "0.08em", textTransform: "uppercase", paddingTop: "3px" }}>
                      {s.label}
                    </dt>
                    <dd style={{ margin: 0, color: colors.ink3 }}>{s.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </>
          )}

          {meta.uses && meta.uses.length > 0 && (
            <>
              <h2
                style={{
                  fontFamily: fonts.serif,
                  fontSize: "22px",
                  fontWeight: 600,
                  margin: "36px 0 14px",
                }}
              >
                Typical applications
              </h2>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "20px",
                  fontSize: "var(--fs-body)",
                  lineHeight: 1.75,
                  color: colors.ink3,
                  maxWidth: "60ch",
                }}
              >
                {meta.uses.map((u) => (
                  <li key={u}>{u}</li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div>
          <div
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: "4px",
              overflow: "hidden",
              background: colors.white,
            }}
          >
            <img
              src={item.image}
              alt={item.alt || item.name}
              width={520}
              height={390}
              loading="eager"
              fetchpriority="high"
              decoding="sync"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              padding: "24px 22px",
              border: `1px solid ${colors.border}`,
              borderRadius: "4px",
              background: colors.panel,
            }}
          >
            <div style={{ fontSize: "15px", color: colors.ink3, lineHeight: 1.6 }}>
              Tell us the grammage, size and quantity you need and we'll come
              back with availability and a trade price.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "18px" }}>
              {/* No padding override: Btn is fluid now, and a fixed 13px 20px
                  pulled it back under the 44px floor at the small end. */}
              <Btn
                variant="dark"
                onClick={() => setEnquiring(true)}
                style={{ justifyContent: "center" }}
              >
                {/* See the note on the same label in CategoryPage. */}
                {canSubmitDirectly ? "Send an enquiry" : "Enquire on WhatsApp"}
              </Btn>
              {/* Highest-intent control on the page and it was a 27px target.
                  Flex + minHeight blocks it out to 46px without moving the
                  number visually. */}
              <PhoneLink
                style={{
                  fontFamily: fonts.mono,
                  fontSize: "var(--fs-body)",
                  color: colors.ink,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "46px",
                  padding: "12px 0",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <FaqSection
        faqs={meta.faqs}
        eyebrow="Common questions"
        title={`${item.name} — what buyers ask`}
      />

      {siblings.length > 0 && (
        <section style={{ paddingTop: "56px" }}>
          <h2
            style={{
              fontFamily: fonts.serif,
              fontSize: "24px",
              fontWeight: 500,
              margin: "0 0 18px",
            }}
          >
            Also in {cat.name}
          </h2>
          <div
            style={{
              display: "grid",
              // min(240px,100%) keeps the track floor from exceeding the
              // column: a bare 240px minimum survives inside a 280px phone
              // column and pushes the document sideways.
              gridTemplateColumns: "repeat(auto-fit,minmax(min(240px,100%),1fr))",
              gap: "12px",
            }}
          >
            {siblings.map((p) => (
              <Link
                key={p.slug}
                to={productPath(cat.slug, p.slug)}
                className="se-card"
                style={{
                  cursor: "pointer",
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                  background: colors.white,
                  padding: "16px 18px",
                  fontSize: "15px",
                  fontWeight: 600,
                }}
              >
                {p.name} →
              </Link>
            ))}
          </div>
        </section>
      )}

      {enquiring && (
        <ProductEnquiryModal
          item={item}
          categoryName={cat.name}
          onClose={() => setEnquiring(false)}
        />
      )}
    </main>
  );
}
